import { Component, Input, ChangeDetectorRef, inject } from '@angular/core';
import { LogError, RunView } from '@memberjunction/core';
import { marketingAgentLearningEntity, marketingExperimentEntity } from 'mj_generatedentities';

@Component({
    standalone: false,
    selector: 'mkt-ws-experiments-learning-detail',
    template: `
<div *ngIf="loading" class="ld__loading"><i class="fa-solid fa-spinner fa-spin"></i> Loading finding…</div>

<ng-container *ngIf="!loading && !learning">
    <mkt-ws-page-head
        eyebrow="Learning"
        title="No finding selected"
        sub="Pick a finding from the Learnings list."
        [hasActions]="true">
        <div actions>
            <button class="ld__btn" type="button" (click)="navigate('experiments/learnings')"><i class="fa-solid fa-arrow-left"></i> All learnings</button>
        </div>
    </mkt-ws-page-head>
</ng-container>

<ng-container *ngIf="!loading && learning">
    <div class="ld__crumbs">
        <button type="button" class="ld__crumb" (click)="navigate('experiments/learnings')">Learnings</button>
        <span>›</span>
        <span class="ld__crumb-current">{{ isAntiPattern ? 'Anti-pattern' : 'Positive finding' }}</span>
    </div>
    <mkt-ws-page-head
        [eyebrow]="'Learning · ' + (isAntiPattern ? 'Anti-pattern' : 'Positive') + ' · ' + (learning.Source || '—')"
        [title]="learning.Finding || 'Learning'"
        [sub]="confidenceSub">
    </mkt-ws-page-head>

    <div class="ld__class">
        <mkt-ws-pill *ngIf="isAntiPattern" variant="bad"><i class="fa-solid fa-shield-halved"></i> Anti-pattern</mkt-ws-pill>
        <mkt-ws-pill *ngIf="!isAntiPattern" variant="good"><i class="fa-solid fa-sparkles"></i> Positive</mkt-ws-pill>
        <mkt-ws-pill variant="muted">{{ learning.Source || '—' }}</mkt-ws-pill>
        <mkt-ws-pill [variant]="confVariant">Confidence · {{ learning.Confidence || '—' }}</mkt-ws-pill>
        <mkt-ws-pill *ngIf="learning.IsPortfolioLevel" variant="info">Cross-company</mkt-ws-pill>
        <span class="ld__ts">Recorded {{ formatDate(learning.DateRecorded) }}</span>
    </div>

    <div class="ld__layout">
        <div class="ld__main">
            <mkt-ws-card [hasHead]="true">
                <div head><span class="ld__caps-ink">Finding</span></div>
                <div class="ld__body">{{ learning.Finding }}</div>
            </mkt-ws-card>

            <mkt-ws-card *ngIf="learning.Evidence" [hasHead]="true">
                <div head><span class="ld__caps-ink">Evidence</span></div>
                <div class="ld__body">{{ learning.Evidence }}</div>
            </mkt-ws-card>

            <mkt-ws-card *ngIf="relatedExperiment" [hasHead]="true" [noPadding]="true">
                <div head><span class="ld__caps-ink">Related experiment</span></div>
                <button type="button" class="ld__exp-row" (click)="openExperiment(relatedExperiment.ID)">
                    <span class="ld__mono ld__caps">{{ relatedExperiment.Code || relatedExperiment.ID.slice(0,8) }}</span>
                    <span class="ld__exp-name">{{ relatedExperiment.Name || relatedExperiment.Hypothesis }}</span>
                    <mkt-ws-pill variant="muted">{{ relatedExperiment.ExperimentStatus }}</mkt-ws-pill>
                </button>
            </mkt-ws-card>
        </div>

        <div class="ld__side">
            <mkt-ws-card [hasHead]="true">
                <div head><span class="ld__caps-ink">Re-test triggers</span></div>
                <div class="ld__body" style="font-size: 12.5px;">
                    <ul style="margin: 0; padding-left: 16px;">
                        <li>Targeting model changes materially</li>
                        <li>Budget tier or scope changes substantially</li>
                        <li>Channel maturity shifts (proven → testing)</li>
                        <li>Review window: 12 months from {{ formatDate(learning.DateRecorded) }}</li>
                    </ul>
                </div>
            </mkt-ws-card>

            <mkt-ws-card *ngIf="learning.IsPortfolioLevel" [hasHead]="true">
                <div head><span class="ld__caps-ink">Cross-company</span></div>
                <div class="ld__body" style="font-size: 12.5px;">
                    Marked as portfolio-level — surfaced as a default check across other companies once they come online.
                </div>
            </mkt-ws-card>
        </div>
    </div>
</ng-container>
    `,
    styles: [`
        :host { display: block; }
        .ld__loading { padding: 60px 20px; text-align: center; color: var(--mj-text-secondary, #71717a); }
        .ld__btn { display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px; height: 30px; border: 1px solid var(--mj-border-default, #e0e0e0); background: var(--mj-bg-surface, #fff); border-radius: 6px; font: inherit; font-size: 12px; cursor: pointer; color: var(--mj-text-primary, #1a1a1a); }
        .ld__btn:hover { background: var(--mj-bg-page, #f5f5f5); }

        .ld__caps, .ld__caps-ink { font-size: 10.5px; letter-spacing: 0.11em; text-transform: uppercase; font-weight: 500; color: var(--mj-text-secondary, #71717a); }
        .ld__caps-ink { color: var(--mj-text-primary, #1a1a1a); }
        .ld__body { font-size: 13.5px; line-height: 1.7; color: var(--mj-text-primary, #1a1a1a); }
        .ld__mono { font-family: ui-monospace, monospace; font-variant-numeric: tabular-nums; }

        .ld__crumbs { display: flex; align-items: center; gap: 6px; font-size: 12px; color: var(--mj-text-secondary, #71717a); margin-bottom: 14px; }
        .ld__crumb { background: none; border: none; padding: 0; font: inherit; color: var(--mj-text-secondary, #71717a); cursor: pointer; }
        .ld__crumb:hover { color: var(--mj-text-primary, #1a1a1a); }
        .ld__crumb-current { color: var(--mj-text-primary, #1a1a1a); }

        .ld__class { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 24px; }
        .ld__ts { margin-left: auto; font-size: 11.5px; color: var(--mj-text-secondary, #71717a); font-family: ui-monospace, monospace; }

        .ld__layout { display: grid; grid-template-columns: 2fr 1fr; gap: 24px; align-items: start; }
        .ld__main, .ld__side { display: flex; flex-direction: column; gap: 16px; }

        .ld__exp-row { display: flex; align-items: center; gap: 12px; padding: 12px 16px; background: transparent; border: none; cursor: pointer; font: inherit; text-align: left; transition: background 0.15s; width: 100%; }
        .ld__exp-row:hover { background: var(--mj-bg-page, #f5f5f5); }
        .ld__exp-name { flex: 1; font-size: 13px; color: var(--mj-text-primary, #1a1a1a); }

        @media (max-width: 1024px) {
            .ld__layout { grid-template-columns: 1fr; }
        }
    `]
})
export class ExperimentsLearningDetailSection {
    private _companyId = '';
    private _selectedId = '';

    @Input() set companyId(v: string) {
        if (v && v !== this._companyId) {
            this._companyId = v;
            void this.load();
        }
    }
    get companyId(): string { return this._companyId; }

    @Input() set selectedId(v: string) {
        if (v !== this._selectedId) {
            this._selectedId = v;
            void this.load();
        }
    }
    get selectedId(): string { return this._selectedId; }

    @Input() navigate: (path: string) => void = () => {};
    @Input() navigateWithId: (path: string, id?: string) => void = () => {};

    private cdr = inject(ChangeDetectorRef);

    loading = false;
    learning: marketingAgentLearningEntity | null = null;
    relatedExperiment: marketingExperimentEntity | null = null;

    get isAntiPattern(): boolean {
        return /anti.?pattern|don.?t|do not|fail/i.test(this.learning?.Finding || '');
    }
    get confidenceSub(): string {
        if (!this.learning) return '';
        const conf = this.learning.Confidence || '—';
        return `${conf}-confidence finding · recorded ${this.formatDate(this.learning.DateRecorded)}.`;
    }
    get confVariant(): 'good' | 'warn' | 'bad' | 'muted' {
        switch (this.learning?.Confidence) {
            case 'High': return 'good';
            case 'Medium': return 'warn';
            case 'Low': return 'bad';
            default: return 'muted';
        }
    }
    formatDate(d: Date | string | null | undefined): string {
        if (!d) return '—';
        return new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    }

    openExperiment(id: string): void {
        this.navigateWithId('experiments/detail', id);
    }

    private async load(): Promise<void> {
        if (!this._selectedId || !this._companyId) {
            this.learning = null;
            this.relatedExperiment = null;
            this.cdr.detectChanges();
            return;
        }
        this.loading = true;
        this.cdr.detectChanges();
        try {
            const rv = new RunView();
            const r = await rv.RunView<marketingAgentLearningEntity>({
                EntityName: 'Agent Learnings',
                ExtraFilter: `ID='${this._selectedId}'`,
                MaxRows: 1,
                ResultType: 'entity_object',
            });
            if (r.Success && r.Results && r.Results.length > 0) {
                this.learning = r.Results[0];
                if (this.learning.RelatedExperimentID) {
                    const er = await rv.RunView<marketingExperimentEntity>({
                        EntityName: 'Experiments',
                        ExtraFilter: `ID='${this.learning.RelatedExperimentID}'`,
                        MaxRows: 1,
                        ResultType: 'simple',
                    });
                    if (er.Success && er.Results && er.Results.length > 0) {
                        this.relatedExperiment = er.Results[0];
                    }
                } else {
                    this.relatedExperiment = null;
                }
            }
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : String(err);
            LogError(`[ExperimentsLearningDetail] load failed: ${msg}`);
        } finally {
            this.loading = false;
            this.cdr.detectChanges();
        }
    }
}
